/**
 * Socket.IO integration.
 *
 * - `socketOn(event, cb)` — subscribe to a typed event (works before connection)
 * - `useSocketConnection()` — call once in App.vue, wires auth lifecycle
 * - `useSocketEvent(event, cb)` — subscribe with auto-cleanup on unmount
 *
 *  Example:
 *  ```ts
 *  onStaleDataRefetchKey(refetchKeys.KYC_STATUS, () => {
 *    // refetch KYC status
 *  });
 *
 *  onStaleDataRefetchKey(refetchKeys.ACCOUNTS, () => {
 *    // refetch accounts
 *  });
 *  ```
 */

import { io, type Socket } from 'socket.io-client';
import { getCurrentInstance, onUnmounted, readonly, ref, watch } from 'vue';
import { authEventBus } from '../auth0/auth-event-bus';
import { useAuthSessionStore } from '../auth0/store/auth-session.store';
import { appConfig } from '../providers/config';
import {
  ClientToServerEvents,
  refetchKeys,
  ServerEventPayload,
  ServerToClientEvents,
  type StaleDataEventPayload,
} from './socket.types';
import { dispatchStaleDataPayload, registerStaleDataRefetchHandler } from './stale-data-dispatcher';
import {
  claimStaleDataTabMessage,
  publishStaleDataTabMessage,
  subscribeToStaleDataTabMessages,
} from './stale-data-tab-sync';

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

type SocketCredentials = {
  token: string;
  idToken: string;
};

let socket: TypedSocket | null = null;
const connected = ref(false);
let consecutiveErrors = 0;
const MAX_AUTH_RETRIES = 3;

/** Persistent listener registry — survives reconnections, works pre-connect. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const registry = new Map<string, Set<(...args: any[]) => void>>();

const attachRegistry = (sock: TypedSocket) => {
  for (const [event, listeners] of registry) {
    for (const fn of listeners) sock.on(event as never, fn as never);
  }
};

const toSocketAuth = ({ token, idToken }: SocketCredentials) => {
  return {
    token,
    idToken,
  };
};

const createSocket = (url: string, credentials: SocketCredentials) => {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
  }

  socket = io(url, {
    auth: toSocketAuth(credentials),
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 30_000,
  }) as TypedSocket;

  socket.on('connect', () => {
    connected.value = true;
    consecutiveErrors = 0;
    console.debug('[Socket] connected', socket?.id);
  });

  socket.on('disconnect', (reason) => {
    connected.value = false;
    console.debug('[Socket] disconnected', reason);
  });

  socket.io.on('reconnect', (attempt) => {
    console.debug('[Socket] reconnected after', attempt, 'attempts');
  });

  socket.on('connect_error', (err) => {
    consecutiveErrors++;
    console.warn('[Socket] connection error', {
      message: err.message,
      attempt: consecutiveErrors,
    });

    // don't ddos our backend
    if (consecutiveErrors >= MAX_AUTH_RETRIES) {
      console.warn('[Socket] too many consecutive failures, stopping reconnection');
      socket?.disconnect();
    }
  });

  attachRegistry(socket);
};

const destroySocket = () => {
  if (!socket) return;
  socket.removeAllListeners();
  socket.disconnect();
  socket = null;
  connected.value = false;
  consecutiveErrors = 0;
};

/** Subscribe to a typed server event. Returns an unsubscribe function. */
export const socketOn = <E extends keyof ServerToClientEvents>(
  event: E,
  listener: ServerToClientEvents[E],
): (() => void) => {
  if (!registry.has(event as string)) registry.set(event as string, new Set());

  registry.get(event as string)!.add(listener as never);
  socket?.on(event, listener as never);

  return () => {
    registry.get(event as string)?.delete(listener as never);
    socket?.off(event, listener as never);
  };
};

/** Reactive connection state (readonly). */
export const isSocketConnected = readonly(connected);

// ---------------------------------------------------------------------------
// Composables
// ---------------------------------------------------------------------------

/** Only call once in App.vue to wire up the lifecycle — connects/disconnects based on auth state. */
export const useSocketConnection = () => {
  const authSession = useAuthSessionStore();

  const handleStaleDataUpdate = (payload: StaleDataEventPayload) => {
    dispatchStaleDataPayload(payload);
  };

  const handleSocketStaleDataUpdate = <E extends 'stale_data_update' | 'client_data_updated'>(
    event: E,
    payload: ServerEventPayload<E>,
  ) => {
    const tabMessage = claimStaleDataTabMessage(event, payload);

    if (tabMessage === null) return;

    handleStaleDataUpdate(payload);
    publishStaleDataTabMessage(tabMessage);
  };

  const handleAuthJwtUpdate = async () => {
    const currentToken = authSession.jwtToken;

    if (!currentToken) {
      console.debug('[Socket] Skipping AUTH_JWT refresh because there is no current token');
      return;
    }

    try {
      await authSession.refreshAuthClaims();
      console.debug('[Socket] AUTH_JWT claims refreshed');
    } catch (error) {
      console.warn('[Socket] failed to refresh auth session after AUTH_JWT update', error);
    }
  };

  const connectSocket = () => {
    const url = appConfig.VITE_WEBSOCKET_URL;
    const token = authSession.jwtToken;
    const idToken = authSession.idToken;

    if (!url || !token || !idToken) return;

    createSocket(url, { token, idToken });
  };

  const handleTokenChange = (
    [newToken, newIdToken]: [string | null, string | null],
    previousTokens?: [string | null, string | null],
  ) => {
    const [oldToken, oldIdToken] = previousTokens ?? [null, null];

    // Logged out or token cleared
    if (!newToken || !newIdToken) {
      destroySocket();
      return;
    }

    // First login — establish connection
    if (!oldToken || !oldIdToken) {
      connectSocket();
      return;
    }

    // Token refreshed — update auth and reconnect
    if ((newToken !== oldToken || newIdToken !== oldIdToken) && socket) {
      socket.auth = toSocketAuth({ token: newToken, idToken: newIdToken });
      if (socket.connected) socket.disconnect().connect();
    }
  };

  const stopTokenWatch = watch(
    () => [authSession.jwtToken, authSession.idToken] as [string | null, string | null],
    handleTokenChange,
    { immediate: true },
  );

  const unsubscribeStaleDataUpdate = socketOn('stale_data_update', (payload) => {
    handleSocketStaleDataUpdate('stale_data_update', payload);
  });
  const unsubscribeClientDataUpdated = socketOn('client_data_updated', (payload) => {
    handleSocketStaleDataUpdate('client_data_updated', payload);
  });
  const unsubscribeStaleDataTabMessages = subscribeToStaleDataTabMessages((_event, payload) => {
    handleStaleDataUpdate(payload);
  });
  const unregisterAuthRefreshHandler = registerStaleDataRefetchHandler(refetchKeys.AUTH_JWT, () => {
    console.debug('[Socket] Received AUTH_JWT stale data update');
    void handleAuthJwtUpdate();
  });
  const unsubLogout = authEventBus.subscribe('logout', () => destroySocket());

  onUnmounted(() => {
    stopTokenWatch();
    unsubscribeStaleDataUpdate();
    unsubscribeClientDataUpdated();
    unregisterAuthRefreshHandler();
    unsubscribeStaleDataTabMessages();
    unsubLogout();
    destroySocket();
  });

  return { isConnected: isSocketConnected };
};

/** Subscribe to a server event with auto-cleanup on component unmount. */
export const useSocketEvent = <E extends keyof ServerToClientEvents>(
  event: E,
  callback: (payload: ServerEventPayload<E>) => void,
) => {
  const unsubscribe = socketOn(event, callback as ServerToClientEvents[E]);

  if (getCurrentInstance()) {
    onUnmounted(unsubscribe);
  }

  return { unsubscribe };
};
